/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import clsx from 'clsx';
import { Field, useFormikContext, setIn, FastField } from 'formik';
import { useEventCallback } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import uniqid from 'uniqid';
import { DESTINATION_TYPES } from '../../../containers/destination/constants/destinationTypes';
import { PARTITIONING_IN_BIGQUERY_DOC_URL } from '../../../core/constants/constants';
import { InclusionItem } from '../../../core/interfaces/inclusion-list-v2';
import { RefreshService } from '../../../core/services/refresh.service';
import { getErrorMessageFromObj } from '../../../core/utils/request';
import { matchPattern } from '../../../core/utils/string';
import { FormState, IField } from '../interfaces/form';
import { isFireboltOptionalKeyDisabled } from '../utils/schemaBuilder';
import { FireboltColumnOrderAlertPane } from './FireboltColumnOrderAlertPane';
import { FireboltSchemaBuilderHelperPane } from './FireboltSchemaBuilderHelperPane';
import { FireboltTableTypeSelectionCardView } from './FireboltTableTypeSelectionCardView';
import { useProgressiveLoading } from '../../../list/hooks/useProgressiveLoading';
import {
  SCHEMA_BUILDER_TABLE_LENGTH_CHECK_LIMIT,
  TABLE_TYPES_INFO_DATA,
  DISABLED_PRIMARY_KEY_DATA_TYPES,
  BIGQUERY_PARTITION_TYPES,
  BIG_QUERY_CLUSTER_KEY_DATA_TYPES,
  BIGQUERY_PARTITION_STYLES
} from '../constants/constants';
import { SchemaBuilderStrings } from '../constants/strings';
import useSettingsStorageService from '../../../auth/hooks/useSettingsStorageService';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import useForceUpdate from '../../../core/hooks/useForceUpdate';
import useInclusionList from '../../../list/hooks/useInclusionList';
import useScrollToTopOrBottom from '../../../a11y/scroll/hooks/useScrollToTopOrBottom';
import useService from '../../../injector/hooks/useService';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import { useConfirm } from '../../../modal/components/confirm-dialog-v1/ConfirmDialogV1Provider';
import {
  HdFormikDropDown,
  HdFormikTextField,
  shouldUpdateFormikTextFastField
} from '../../../form/formik/components';
import { SchemaBuilderRowMemo, SchemaBuilderRow } from './SchemaBuilderRow';
import { HdCoordinatedScroll, HdCoordinatedScrollAnchor } from '../../../a11y/scroll/components/coordinated-scroll/HdCoordinatedScroll';
import ScrollToTopButton from '../../../a11y/scroll/components/scroll-to-top-button/ScrollToTopButton';
import SearchArea from '../../../list/components/search-area-v2/SearchAreaV2';

import {
  HdButton,
  HdDocLink,
  HdIcon,
  HdIconButton,
  HdPane,
  HdTooltip,
  HdCheckbox
} from '../../../components';
import styles from './styles.module.scss';
import { PartitionStylePopUp } from './ToolTip';
import { FireboltWarningDialog } from './WarningDialog';
import { HdCoordinatedHeight } from '../../../layout/components/coordinated-height/HdCoordinatedHeight';
import {
  BigQueryPartitionStyleEnum,
  BigQueryPartitionTypeEnum,
  FireboltTableTypeEnum
} from '../interfaces/interface';
import { TRACKER_FIELDS_INFO_TOOL_CLICK, TRACKER_TABLE_TYPE_DROPDOWN_CLICK } from '../tracking/schemaBuilderTracking';

function HelperTextRender({ option }) {
  return <div className='p-3'>{option?.description}</div>;
}

const isPrimaryKeyDisabled = dataType =>
  DISABLED_PRIMARY_KEY_DATA_TYPES.indexOf(dataType?.type_display) !== -1;

const dataIdGenerator = getDataIdGenerator('schema-builder');

export function SchemaBuilderForm({
  dataTypes,
  dataTypesLoading,
  options,
  formikRef,
  handleCancelClick,
  hasErrors,
  tableNameLengthLimit,
  initialFields,
  error,
  mappingToCreatedTable,
  creatingTable
}: {
  initialFields: IField[];
  [key: string]: any;
}) {
  const { values, setValues, setFieldValue } = useFormikContext<FormState>();

  const inclusionListCallBack = () => {};

  const { inclusionList, togglePageCheckedState } = useInclusionList(
    'field-selector',
    null,
    inclusionListCallBack,
    null
  );

  const listConductorCheckboxIndeterminate = !!inclusionList.pageIndeterminate;
  const listConductorCheckboxChecked = !!(
    inclusionList.pageIncluded && !inclusionList.pageIndeterminate
  );

  const refreshService = useService(RefreshService);
  const storageSettingsService = useSettingsStorageService();

  const showTableWarningDialog = !storageSettingsService.getSettings(
    'CREATE_TABLE_WARNING_DIALOG_SHOWN'
  );

  const processedOptions = useMemo(
    () => ({
      isFireboltDest: options.destType === DESTINATION_TYPES.FIREBOLT.name,
      isBQDest: options.destType === DESTINATION_TYPES.BIGQUERY.name,
      isDataBricks: options.destType === DESTINATION_TYPES.DATABRICKS.name,
      isRedshift: options.destType === DESTINATION_TYPES.REDSHIFT.name,
      isSnowFlake: options.destType === DESTINATION_TYPES.SNOWFLAKE.name,
      isMySql: options.destType === DESTINATION_TYPES.MYSQL.name,
      withMappingData: options.withMappingData
    }),
    [options]
  );

  const { isBQDest, isDataBricks, isFireboltDest, isRedshift, isSnowFlake } = processedOptions;

  const [searchKey, setSearchKey] = useState('');

  const [showMapperTable, setShowMapperTable] = useState(!isFireboltDest);
  const [showInfoTool, setShowInfoTool] = useState(false);
  const [bigQueryClusterKeysLimitReached, setBigQueryClusterKeysLimitReached] = useState(false);
  const [showFireboltWarning, setShowFireboltWarning] = useState(false);

  const coordinatedHeightTargetRef = useRef();
  const coordinatedScrollAnchorTargetRef = useRef();
  const coordinatedScrollTargetRef = useRef<HTMLDivElement>();
  const uniqueIds = useRef(new Set());
  const highlightIds = useRef(new Map<string, any>());
  const forceUpdate = useForceUpdate();
  const { confirm } = useConfirm();
  const { eventTrack } = useAnalyticsTracker();

  const scrollToBottom = useScrollToTopOrBottom(coordinatedScrollTargetRef);

  const generateIds = () => {
    const newId = uniqid();
    if (!uniqueIds.current.has(newId)) {
      return newId;
    }
    return generateIds();
  };

  useEffect(() => {
    if (initialFields.length > 0) {
      const processedInclusionList: InclusionItem[] = [];

      const processedFields = (initialFields || []).map(field => {
        const id = generateIds();
        const item = new InclusionItem(id, {
          selected: field.inclusionItem?.checked,
          key: id
        });

        processedInclusionList.push(item);

        const intermittentField = {
          ...field,
          dataType: field.destination?.type,
          isPrimaryKey: field?.keys?.some(key => key.abbr === 'pk'),
          inclusionItem: item,
          uniqId: id
        };

        if (isFireboltDest) {
          return {
            ...intermittentField,
            isSortKey: intermittentField.isPrimaryKey,
            optional: !intermittentField.isPrimaryKey
          };
        }

        return intermittentField;
      });

      inclusionList.setState({
        selected: false,
        size: 1,
        entities: processedInclusionList.map(field => ({
          key: field.key,
          selected: field.included
        })),
        key: 'root-key'
      });

      inclusionList.addItems(processedInclusionList);

      setValues(prevValues => ({
        ...prevValues,
        fields: processedFields
      }));
    } else {
      addField();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredFields = useMemo(
    () =>
      values.fields
        .map((field, idx) => ({ ...field, idx }))
        .filter(field => matchPattern(field.destination.name, searchKey)),
    [searchKey, values.fields]
  );

  const { items: filteredFieldsToBeRendered, sentryRef } = useProgressiveLoading(filteredFields);

  const addField = () => {
    setSearchKey('');

    const defaultField: IField = {
      destination: {
        name: ''
      },
      dataType: dataTypes && dataTypes.length ? { ...dataTypes[0] } : null,
      uniqId: generateIds()
    };

    setValues(prevValues => ({
      ...prevValues,
      fields: [...prevValues.fields, defaultField]
    }));

    setTimeout(() => {
      scrollToBottom('bottom');
    }, 100);

    refreshService.scrollParentViewportToBottom();
  };

  const setHighlightOnRow = uniqId => {
    if (highlightIds.current.has(uniqId)) {
      clearTimeout(highlightIds.current.get(uniqId));
    }

    highlightIds.current.set(
      uniqId,
      setTimeout(() => {
        highlightIds.current.delete(uniqId);
        forceUpdate();
      }, 1000)
    );
  };

  const moveRow = (filteredFromIndex: number, filteredToIndex: number) => {
    const filteredFromField = filteredFields[filteredFromIndex];
    const filteredToField = filteredFields[filteredToIndex];

    const fromIndex = filteredFromField.idx;
    const toIndex = filteredToField.idx;

    values.fields[fromIndex] = filteredToField;
    values.fields[toIndex] = filteredFromField;

    setHighlightOnRow(filteredFromField.uniqId);

    setValues(prevState => ({
      ...prevState,
      fields: [...values.fields]
    }));
  };

  const moveRowUp = useEventCallback((idx: number) => {
    if (idx === 0) {
      return;
    }
    moveRow(idx, idx - 1);
  });

  const moveRowDown = useEventCallback((idx: number) => {
    if (idx >= values.fields.length - 1) {
      return;
    }
    moveRow(idx, idx + 1);
  });

  const beforeRowDelete = useEventCallback((field: IField) => {
    confirm({
      title: 'DELETE',
      body: `Are you sure you don't want ${field?.destination?.name || 'this field'} ?`,
      negativeButtonText: 'No',
      positiveButtonText: 'Yes, DELETE this field'
    }).then(res => {
      if (res) {
        const fieldIndex = values.fields.findIndex(
          searchField => searchField.uniqId === field.uniqId
        );

        if (fieldIndex >= 0) {
          values.fields.splice(fieldIndex, 1);
        }

        setValues(prevValues => ({
          ...prevValues,
          fields: [...values.fields]
        }));
      }
    });
  });

  const showLoadingOverlay = creatingTable || mappingToCreatedTable;

  const createTableBtnText = useMemo(() => {
    if (!showMapperTable) {
      return 'Create table';
    }

    if (mappingToCreatedTable) {
      return '';
    }

    if (creatingTable) {
      return 'Creating Table';
    }

    if (options.withMappingData && values.fields.some(field => field.inclusionItem)) {
      return 'Create Table & Map';
    }

    return 'Create table';
  }, [
    creatingTable,
    mappingToCreatedTable,
    options.withMappingData,
    showMapperTable,
    values.fields
  ]);

  /*
   * For BQ, partition key is disabled when
   * 1. partition style is not Field Based
   * 2. field data type is not timestamp in case of HOUR partition type
   * 3. field data type is not timestamp/date in rest of the partition types
   * */
  const isPartitionKeyDisabled = useEventCallback((dataType: any) => {
    if (isDataBricks) {
      return false;
    }
    // eslint-disable-next-line no-nested-ternary
    return values.partitionStyle?.value !== BigQueryPartitionStyleEnum.FIELD_BASED_PARTITION
      ? true
      : values.partitionType === BigQueryPartitionTypeEnum.HOUR
      ? dataType?.type_display !== 'timestamp'
      : !(dataType?.type_display === 'date' || dataType?.type_display === 'timestamp');
  });

  const handlePartitionStyleUpdate = partitionStyle => {
    setValues(prevValues => ({
      ...prevValues,
      partitionStyle,
      partitionKey:
        partitionStyle?.value !== BigQueryPartitionStyleEnum.FIELD_BASED_PARTITION
          ? null
          : prevValues.partitionKey
    }));
  };

  const handlePartitionTypeUpdate = partitionType => {
    setValues(prevValues => ({
      ...prevValues,
      partitionType,
      partitionKey:
        partitionType && isPartitionKeyDisabled(prevValues.partitionKey?.dataType)
          ? null
          : prevValues.partitionKey
    }));
  };

  const createTableButtonClicked = () => {
    if (isFireboltDest && showTableWarningDialog) {
      setShowFireboltWarning(true);
      return;
    }

    formikRef.current.submitForm();
  };

  const onCancelClick = () => {
    handleCancelClick();
  };

  const tableLengthCheckLimit = SCHEMA_BUILDER_TABLE_LENGTH_CHECK_LIMIT;

  const TableNameHelperText = useCallback(
    () => (
      <span className='d-block'>
        A unique name
        {!isBQDest && <span> for the table</span>}
        {tableNameLengthLimit <= tableLengthCheckLimit && (
          <span> with a maximum of {tableNameLengthLimit} characters. </span>
        )}
      </span>
    ),
    [isBQDest, tableLengthCheckLimit, tableNameLengthLimit]
  );

  const handleFieldUpdate = (field: IField, dataType: any, dataPath: string) => {
    const fieldClone = { ...field };

    fieldClone.dataType = dataType;

    if (field.isPrimaryKey && isPrimaryKeyDisabled(dataType)) {
      fieldClone.isPrimaryKey = null;
    }

    if (field.isClusterKey && isBigQueryClusterKeyDisabled(fieldClone)) {
      fieldClone.isClusterKey = null;
    }

    if (
      isFireboltDest &&
      fieldClone.optional &&
      isFireboltOptionalKeyDisabled(fieldClone.dataType?.type)
    ) {
      fieldClone.optional = false;
    }

    const valuesClone = setIn(values, dataPath, fieldClone);

    if (values.partitionKey?.uniqId === field?.uniqId && isPartitionKeyDisabled(dataType)) {
      valuesClone.partitionKey = null;
    }

    setValues(valuesClone);
  };

  /*
   * Big Query Cluster Key related functions
   * BQ Can have a maximum of 4 cluster keys
   * */
  const canSelectBigQueryClusterKey = (field: any, key: string) => {
    // Action - Selected
    if (!field[key]) {
      const noOfClusterKeys = values.fields.filter(
        sourceField => !!sourceField.isClusterKey
      ).length;

      if (noOfClusterKeys === 3) {
        setBigQueryClusterKeysLimitReached(true);
      }
      // Action - Deselected
    } else if (bigQueryClusterKeysLimitReached) {
      setBigQueryClusterKeysLimitReached(false);
    }
  };

  const isBigQueryClusterKeyDisabled = useEventCallback((field: any) => {
    if (!field?.dataType || !field.dataType?.type) {
      return true;
    }

    if (!BIG_QUERY_CLUSTER_KEY_DATA_TYPES.includes(field.dataType.type.toLowerCase())) {
      return true;
    }

    return !field.isClusterKey && bigQueryClusterKeysLimitReached;
  });

  const handleRadioKeyChange = useEventCallback((field: IField, path: string) => {
    setValues(stateValues => ({
      ...stateValues,
      [path]: field
    }));
  });

  const toggleKey = useEventCallback((fieldIdx: number, key: string) => {
    const field = values.fields[fieldIdx];

    if (key === 'isClusterKey' && isBQDest) {
      canSelectBigQueryClusterKey(values.fields[fieldIdx], key);
    }

    if (isFireboltDest) {
      if (key === 'isSortKey' || key === 'isPartitionKey') {
        field.optional = false;
      }
    }

    field[key] = !field[key];

    setValues(prevValues => ({
      ...prevValues,
      fields: [...values.fields]
    }));
  });

  const handleDataTypeChangeEvent = useEventCallback((dataType: any, fieldPath: string) => {
    const idx = fieldPath.match(/fields\[([0-9]*)\]\.dataType/)?.[1];
    handleFieldUpdate(values.fields[idx], dataType, `fields[${idx}]`);
  });

  const tableTypeData = useMemo(() => TABLE_TYPES_INFO_DATA.map(({ tableType }) => tableType), []);

  const loadingOverlayStrings = useMemo(() => {
    if (creatingTable) {
      return SchemaBuilderStrings.creatingTable;
    }

    if (mappingToCreatedTable) {
      return SchemaBuilderStrings.mappingToCreatedTable;
    }
    return null;
  }, [mappingToCreatedTable, creatingTable]);

  const handleFireboltTableTypeChange = tableType => {
    eventTrack({
      action: TRACKER_TABLE_TYPE_DROPDOWN_CLICK,
      properties: {
        tableType: tableType.tableType
      }
    });

    setValues(prevValues => {
      const newValues = { ...prevValues };
      newValues.fireboltTableType = tableType;

      newValues.fields = prevValues.fields.map(oldValue => ({
        ...oldValue,
        isPartitionKey:
          tableType.tableType === FireboltTableTypeEnum.DIMENSION_TABLE
            ? false
            : oldValue.isPartitionKey
      }));
      return newValues;
    });
  };

  return (
    <div className=''>
      <div
        className={clsx(styles.sbHeader, 'surface-box', {
          'd-flex justify-between': !showMapperTable
        })}
      >
        {!showMapperTable && (
          <div className='center-flex-row'>
            <HdIconButton
              className='mr-3'
              onClick={() => onCancelClick()}
              dataId={dataIdGenerator('cancel')}
            >
              <HdIcon name='back' />
            </HdIconButton>
            <strong>Select the Table type</strong>
          </div>
        )}

        {showMapperTable && (
          <div className='d-flex'>
            <div className={clsx(styles.tableName)}>
              <FastField
                label='Table Name'
                name='tableName'
                required
                shouldUpdate={shouldUpdateFormikTextFastField}
                debounceUpdates
                maxLength={tableNameLengthLimit}
                className={styles.tableNameGroup}
                component={HdFormikTextField}
                helperText={<TableNameHelperText />}
              />
            </div>

            {isFireboltDest && (
              <div className={clsx('ml-5', styles.headerDropdown)}>
                <Field
                  label='Table Type'
                  required
                  options={tableTypeData}
                  valueAccessor='tableType'
                  name='fireboltTableType'
                  autoWidthPaperMode
                  selectMode
                  onChangeEventHandler={handleFireboltTableTypeChange}
                  HelperDocumentAdornment={HelperTextRender}
                  disabled={showLoadingOverlay}
                  component={HdFormikDropDown}
                  displayAccessor='displayText'
                />
              </div>
            )}

            {isBQDest && (
              <>
                <div className={clsx('ml-5', styles.headerDropdown)}>
                  <Field
                    options={BIGQUERY_PARTITION_STYLES}
                    label='Partition Style'
                    selectMode
                    valueAccessor='value'
                    displayAccessor='name'
                    name='partitionStyle'
                    onChangeEventHandler={handlePartitionStyleUpdate}
                    helperText={
                      <span className='d-flex'>
                        Segment your data{' '}
                        <HdTooltip
                          mode='surface'
                          placement='right'
                          title={<PartitionStylePopUp />}
                          disableInteractive={false}
                        >
                          <HdIcon name='info' className='info-icon info-icon-sm' />
                        </HdTooltip>
                      </span>
                    }
                    disabled={showLoadingOverlay}
                    component={HdFormikDropDown}
                    required
                  />
                </div>

                {values.partitionStyle &&
                  values.partitionStyle?.value !== BigQueryPartitionStyleEnum.NO_PARTITION && (
                    <div className={clsx('ml-5', styles.headerDropdown)}>
                      <Field
                        component={HdFormikDropDown}
                        name='partitionType'
                        label='Partition Type'
                        selectMode
                        onChangeEventHandler={handlePartitionTypeUpdate}
                        helperText={
                          <span className='d-flex'>
                            Granularity of partitioning
                            <HdTooltip
                              mode='surface'
                              placement='right'
                              disableInteractive={false}
                              title='Choose the time granularity on which you want to divide the table into segments.'
                            >
                              <HdIcon name='info' className='info-icon info-icon-sm' />
                            </HdTooltip>
                          </span>
                        }
                        disabled={showLoadingOverlay}
                        options={BIGQUERY_PARTITION_TYPES}
                        required
                      />
                    </div>
                  )}
              </>
            )}
          </div>
        )}

        <div>
          <HdButton
            variant='outline'
            variation='outline'
            type='button'
            className='mr-3'
            disabled={showLoadingOverlay}
            onClick={() => onCancelClick()}
            dataId={dataIdGenerator('cancel')}
          >
            Cancel
          </HdButton>

          <HdButton
            type='button'
            icon={showLoadingOverlay ? 'testing' : ''}
            className={styles.createTableBtn}
            onClick={() => createTableButtonClicked()}
            disabled={hasErrors || showLoadingOverlay}
            dataId={dataIdGenerator('create')}
          >
            {createTableBtnText}
          </HdButton>
        </div>
      </div>

      {!!error && (
        <HdPane
          variant='error-faded'
          className={`mb-5 ${styles.errorMessage}`}
          disableHide
          icon='error-filled'
        >
          <span className='text-normal'>{getErrorMessageFromObj(error)}</span>
        </HdPane>
      )}

      {!showMapperTable && (
        <div className='center-flex-row justify-center h-75'>
          <FireboltTableTypeSelectionCardView
            onTableTypeSelect={tableTypeInfo => {
              setShowMapperTable(true);
              setShowInfoTool(true);
              setFieldValue('fireboltTableType', tableTypeInfo.tableType);
            }}
          />
        </div>
      )}

      {showMapperTable && (
        <HdCoordinatedScrollAnchor elRef={coordinatedScrollAnchorTargetRef.current}>
          <div className={styles.boxTableContainer} ref={coordinatedScrollAnchorTargetRef}>
            <HdCoordinatedHeight elRef={coordinatedHeightTargetRef.current}>
              <div
                className='box box--table position-relative flex-col'
                ref={coordinatedHeightTargetRef}
              >
                <div className='box__header p-5'>
                  <div className='box__title'>
                    {filteredFields.length} {filteredFields.length === 1 ? 'Field' : 'Fields'}{' '}
                    Available
                  </div>

                  <div className='box__header__actions'>
                    <SearchArea
                      dataId={dataIdGenerator('')}
                      isControlled
                      value={searchKey}
                      placeholder='Search Fields'
                      onSearch={key => setSearchKey(key)}
                      defaultExpanded={false}
                    />
                  </div>

                  {isFireboltDest && (
                    <span className='ml-5'>
                      <HdButton
                        icon='help'
                        variation='push'
                        active={showInfoTool}
                        className='p-2'
                        iconOnly
                        dataId={dataIdGenerator(`fields-info-tool`)}
                        onClick={() => {
                          eventTrack({
                            action: TRACKER_FIELDS_INFO_TOOL_CLICK
                          });
                          setShowInfoTool(!showInfoTool);
                        }}
                      />
                      {showInfoTool ? (
                        <>
                          <span className={styles.arrow} />
                          <span className={styles.arrowBase} />
                        </>
                      ) : null}
                    </span>
                  )}
                </div>

                <div className={styles.boxBody}>
                  <div
                    className={clsx('flex-table', {
                      [styles.withInfoTool]: showInfoTool
                    })}
                  >
                    {isFireboltDest ? <FireboltColumnOrderAlertPane /> : null}

                    <HdCoordinatedScroll elRef={coordinatedScrollTargetRef.current}>
                      <div
                        id='schema-builder-tree'
                        ref={coordinatedScrollTargetRef}
                        className={styles.scrollComponent}
                      >
                        <div className='flex-table__row flex-table__row--header flex-table__row--header-sticky'>
                          <div
                            className={clsx(
                              'flex-table__cell flex-table__cell--w-fixed',
                              styles.flexTableCellReorder,
                              styles.flexTableCell
                            )}
                            data-type='reorder'
                          />

                          {/* with MappingData */}
                          {options.withMappingData && (
                            <>
                              <div
                                className={clsx(
                                  'flex-table__cell  flex-table__cell--w-fixed',
                                  styles.flexTableCell,
                                  styles.flexTableCellMapField
                                )}
                                data-type='map_field'
                              >
                                <HdCheckbox
                                  dataId={dataIdGenerator(`check-fields`)}
                                  onChange={() => {
                                    togglePageCheckedState();
                                    forceUpdate();
                                  }}
                                  value='source-schema-toggle'
                                  checked={listConductorCheckboxChecked}
                                  indeterminate={listConductorCheckboxIndeterminate}
                                />
                              </div>

                              <div
                                className={clsx(
                                  'flex-table__cell flex-table__cell--w-fixed',
                                  styles.flexTableCell,
                                  styles.flexTableCellSourceField
                                )}
                                data-type='source_field'
                              >
                                Source Field
                              </div>
                            </>
                          )}

                          <div
                            className={clsx(
                              'flex-table__cell flex-table__cell--w-fixed',
                              styles.flexTableCell,
                              styles.flexTableCellFieldName,
                              ''
                            )}
                            data-type='field_name'
                          >
                            Destination Field Name
                          </div>

                          <div
                            className={clsx(
                              'flex-table__cell  flex-table__cell--w-fixed',
                              styles.flexTableCell,
                              styles.flexTableCellDataType
                            )}
                            data-type='data_type'
                          >
                            Data Type
                          </div>

                          {/* <!-- PrimaryKey --> */}
                          {!isFireboltDest && (
                            <div
                              className={clsx(
                                'flex-table__cell  flex-table__cell--w-fixed',
                                styles.flexTableCell,
                                styles.flexTableCellKey
                              )}
                              data-type='key'
                            >
                              <div className={clsx(styles.fieldAction)}>
                                Primary Key
                                <HdTooltip
                                  mode='surface'
                                  placement='right'
                                  disableInteractive={false}
                                  title={SchemaBuilderStrings.primaryKeyDescription}
                                >
                                  <HdIcon name='info' className='info-icon info-icon-sm' />
                                </HdTooltip>
                              </div>
                            </div>
                          )}

                          {/* <!-- PartitionKey --> */}
                          {(isBQDest || isDataBricks) && (
                            <div
                              className={clsx(
                                'flex-table__cell  flex-table__cell--w-fixed',
                                styles.flexTableCell,
                                styles.flexTableCellKey
                              )}
                              data-type='key'
                            >
                              <div className={clsx(styles.fieldAction)}>
                                Partition Key
                                <HdTooltip
                                  mode='surface'
                                  placement='right'
                                  disableInteractive={false}
                                  title={
                                    <>
                                      {SchemaBuilderStrings.bigqueryPartitionKeyDescription}
                                      <HdDocLink
                                        section='create_table'
                                        dataId={dataIdGenerator(`partition-bigquery`)}
                                        docLink={PARTITIONING_IN_BIGQUERY_DOC_URL}
                                      />
                                    </>
                                  }
                                >
                                  <HdIcon name='info' className='info-icon info-icon-sm' />
                                </HdTooltip>
                              </div>
                            </div>
                          )}

                          {/* Cluster Key */}
                          {(isBQDest || isSnowFlake) && (
                            <div
                              className={clsx(
                                'flex-table__cell  flex-table__cell--w-fixed',
                                styles.flexTableCell,
                                styles.flexTableCellKey
                              )}
                              data-type='key'
                            >
                              Cluster Key
                              <HdTooltip
                                mode='surface'
                                placement='right'
                                disableInteractive={false}
                                title={SchemaBuilderStrings.clusterKeyDescription}
                              >
                                <HdIcon name='info' className='info-icon info-icon-sm' />
                              </HdTooltip>
                            </div>
                          )}

                          {/* Firebolt */}
                          {isFireboltDest && (
                            <>
                              <div
                                className={clsx(
                                  'flex-table__cell',
                                  styles.flexTableCell,
                                  styles.flexTableCellKey,
                                  styles.flexTableCellShort
                                )}
                                data-type='key'
                              >
                                <HdTooltip
                                  mode='surface'
                                  placement='right'
                                  disableInteractive={false}
                                  title='Primary Index'
                                >
                                  <span>PI</span>
                                </HdTooltip>
                              </div>

                              <div
                                className={clsx(
                                  'flex-table__cell',
                                  styles.flexTableCell,
                                  styles.flexTableCellKey,
                                  styles.flexTableCellShort
                                )}
                                data-type='key'
                              >
                                <HdTooltip
                                  mode='surface'
                                  placement='right'
                                  disableInteractive={false}
                                  title='Partition Key'
                                >
                                  <span>PRK</span>
                                </HdTooltip>
                              </div>

                              <div
                                className={clsx(
                                  'flex-table__cell',
                                  styles.flexTableCell,
                                  styles.flexTableCellKey,
                                  styles.flexTableCellShort
                                )}
                                data-type='key'
                              >
                                <HdTooltip
                                  mode='surface'
                                  placement='right'
                                  disableInteractive={false}
                                  title='Nullable Column'
                                >
                                  <span>NC</span>
                                </HdTooltip>
                              </div>
                            </>
                          )}

                          {/* REDSHIFT SPECIFIC */}
                          {isRedshift && (
                            <>
                              <div
                                className={clsx(
                                  'flex-table__cell  flex-table__cell--w-fixed',
                                  styles.flexTableCell,
                                  styles.flexTableCellKey
                                )}
                                data-type='key'
                              >
                                Sort Key
                                <HdTooltip
                                  mode='surface'
                                  placement='right'
                                  disableInteractive={false}
                                  title={SchemaBuilderStrings.sortKeyDescription}
                                >
                                  <HdIcon name='info' className='info-icon info-icon-sm' />
                                </HdTooltip>
                              </div>

                              <div
                                className={clsx(
                                  'flex-table__cell  flex-table__cell--w-fixed',
                                  styles.flexTableCell,
                                  styles.flexTableCellKey
                                )}
                                data-key='dist'
                                data-type='key'
                              >
                                Dist Key
                                <HdTooltip
                                  mode='surface'
                                  placement='right'
                                  disableInteractive={false}
                                  title={SchemaBuilderStrings.distKeyDescription}
                                >
                                  <HdIcon name='info' className='info-icon info-icon-sm' />
                                </HdTooltip>
                              </div>
                            </>
                          )}

                          {!options.withMappingData && (
                            <div
                              className={clsx(
                                'flex-table__cell',
                                styles.flexTableCell,
                                styles.moreActions
                              )}
                            />
                          )}
                        </div>

                        {(filteredFieldsToBeRendered || []).map((field, idx) => (
                          <SchemaBuilderRow
                            beforeRowDelete={beforeRowDelete}
                            bigQueryClusterKeysLimitReached={bigQueryClusterKeysLimitReached}
                            dataTypes={dataTypes}
                            field={field}
                            dataTypesLoading={dataTypesLoading}
                            fieldsLength={filteredFieldsToBeRendered.length}
                            fireboltTableType={values.fireboltTableType}
                            forceUpdate={forceUpdate}
                            handleDataTypeChangeEvent={handleDataTypeChangeEvent}
                            handleRadioKeyChange={handleRadioKeyChange}
                            toggleKey={toggleKey}
                            highlightIds={highlightIds}
                            idx={idx}
                            moveRowDown={moveRowDown}
                            moveRowUp={moveRowUp}
                            isBigQueryClusterKeyDisabled={isBigQueryClusterKeyDisabled}
                            isPartitionKeyDisabled={isPartitionKeyDisabled}
                            isPrimaryKeyDisabled={isPrimaryKeyDisabled}
                            options={processedOptions}
                            key={field.uniqId}
                            selectedDistKey={values.distKey?.uniqId}
                            selectedPartitionKey={values.partitionKey?.uniqId}
                          />
                        ))}

                        <div ref={sentryRef} />
                        <ScrollToTopButton scrollViewportId='schema-builder-tree' />
                      </div>
                    </HdCoordinatedScroll>

                    {values.fields.length > 0 && !filteredFields?.length ? (
                      <div className='flex-table__row flex-table__row--no-border'>
                        <div className={clsx('flex-table__cell', styles.flexTableCell, ' py-3')}>
                          No fields found for above search criteria.
                        </div>
                      </div>
                    ) : null}

                    {!values.fields.length && options.addNewFields ? (
                      <div className='flex-table__row flex-table__row--no-border'>
                        <div className={clsx('flex-table__cell', styles.flexTableCell, ' py-3')}>
                          No existing fields. Please create a new field.
                        </div>
                      </div>
                    ) : null}

                    {options.addNewFields && (
                      <div
                        className={clsx(
                          'flex-table__row flex-table__row--no-border flex-shrink-0',
                          {
                            'flex-table__row--odd': values.fields.length % 2 !== 0
                          }
                        )}
                      >
                        <div
                          className={clsx(
                            'flex-table__cell',
                            styles.flexTableCell,
                            ' py-3 justify-center'
                          )}
                        >
                          <HdButton
                            type='button'
                            variation='flat'
                            icon='plus'
                            onClick={() => addField()}
                            dataId={dataIdGenerator('add-field')}
                          >
                            Add Field
                          </HdButton>
                        </div>
                      </div>
                    )}
                  </div>

                  {showInfoTool ? <FireboltSchemaBuilderHelperPane /> : null}
                </div>

                {showLoadingOverlay && (
                  <div className={clsx(styles.loadingOverlay, 'center-flex-row justify-center')}>
                    <div className='no-item-box'>
                      <div className='no-item-box-icon-container'>
                        <HdIcon className='no-item-box-icon' name={loadingOverlayStrings.icon} />
                      </div>

                      <p className='no-item-box-title'>{loadingOverlayStrings.title}</p>

                      <p className='no-item-box-desc'>{loadingOverlayStrings.body}</p>
                    </div>
                  </div>
                )}
              </div>
            </HdCoordinatedHeight>
          </div>
        </HdCoordinatedScrollAnchor>
      )}

      <FireboltWarningDialog
        open={showFireboltWarning}
        onClose={() => setShowFireboltWarning(false)}
        onConfirm={() => {
          setShowFireboltWarning(false);
          formikRef.current.submitForm();
        }}
      />
    </div>
  );
}
